import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth, signOut } from '../AuthContext';
import { useJobs, jobIsActive } from '../data/firestore';
import AgentDock from '../agent/AgentDock';
import SITE from '../site';
import { useNihiColorTheme } from '../nihiTheme';

// Public links in per-site order (SITE.navOrder); auth-only links keep a
// fixed order after them.
const PUBLIC_LINKS = {
  home: { to: '/', label: 'Home', end: true },
  movies: { to: '/movies', label: 'Movies' },
  forecast: { to: '/forecast', label: 'Forecast' },
  rote: { to: '/rote', label: 'Rote' },
  swellmap: { to: '/swell-map', label: 'Swell Map' },
  globe: { to: '/globe', label: '3D Globe' },
  trips: { to: '/trips', label: 'Trips' },
};

export default function Navbar() {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const jobs = useJobs(100, !!user);
  const activeJobs = (jobs || []).filter(jobIsActive).length;
  const { enabled: themeToggle, theme: colorTheme, toggle: toggleColorTheme } = useNihiColorTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Collapse the mobile menu whenever we navigate to a new route.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const close = () => setOpen(false);

  return (
    <nav className={`cs-nav ${scrolled || open ? 'scrolled' : ''} ${open ? 'open' : ''}`}>
      <Link to="/" className="cs-brand" onClick={close}>
        {SITE.logo && (
          <img
            className={SITE.logoWordmark ? 'cs-brand-lockup' : 'cs-brand-logo'}
            src={SITE.logo}
            alt={SITE.logoWordmark ? SITE.siteName : ''}
            aria-hidden={SITE.logoWordmark ? undefined : true}
          />
        )}
        {SITE.theme === 'nihi' && (
          <svg className="cs-brand-mark" viewBox="0 0 32 32" aria-hidden="true">
            <circle cx="16" cy="16" r="14.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path
              d="M6 19.5c3.4 0 4.6-5 8-5s4.6 5 8 5c2.2 0 3.2-2 4-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        )}
        {!SITE.logoWordmark && (
          <>
            {SITE.brandA}<span>{SITE.brandB}</span>
          </>
        )}
      </Link>
      <button
        type="button"
        className="cs-nav-burger"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
      >
        {open ? '✕' : '☰'}
      </button>
      <div className="cs-nav-links">
        {SITE.navOrder.map((k) => {
          const l = PUBLIC_LINKS[k];
          if (!l) return null;
          return (
            <NavLink key={k} to={l.to} end={l.end} onClick={close}>
              {l.label}
            </NavLink>
          );
        })}
        {user && (
          <>
            <NavLink to="/photos" onClick={close}>Photos</NavLink>
            <NavLink to="/proofs" onClick={close}>Proofs</NavLink>
            <NavLink to="/costs" onClick={close}>Costs</NavLink>
            <NavLink to="/visitors" onClick={close}>Visitors</NavLink>
            <NavLink to="/jobs" className="cs-nav-jobs" onClick={close}>
              Processes
              {activeJobs > 0 && <span className="cs-nav-jobs-badge">{activeJobs}</span>}
            </NavLink>
            <NavLink to="/docs" onClick={close}>Docs</NavLink>
          </>
        )}
      </div>
      <div className="cs-nav-spacer" />
      <div className="cs-nav-user">
        {themeToggle && (
          <button
            type="button"
            className="cs-btn cs-btn-ghost cs-btn-sm cs-theme-toggle"
            onClick={toggleColorTheme}
            aria-label={colorTheme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
            title={colorTheme === 'light' ? 'Dark theme' : 'Light theme'}
          >
            {colorTheme === 'light' ? (
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12.1 2.2a.8.8 0 0 0-1 .9 7.6 7.6 0 1 0 9.8 9.8.8.8 0 0 0-.9-1A6 6 0 0 1 12.1 2.2z"
                />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <circle cx="12" cy="12" r="4" fill="currentColor" />
                <path
                  fill="currentColor"
                  d="M12 2.5a.9.9 0 0 1 .9.9v1.4a.9.9 0 1 1-1.8 0V3.4a.9.9 0 0 1 .9-.9zm0 15.3a.9.9 0 0 1 .9.9v1.4a.9.9 0 1 1-1.8 0v-1.4a.9.9 0 0 1 .9-.9zM3.4 11.1a.9.9 0 0 0 0 1.8h1.4a.9.9 0 1 0 0-1.8H3.4zm15.3 0a.9.9 0 1 0 0 1.8h1.4a.9.9 0 1 0 0-1.8h-1.4zM5.6 4.7a.9.9 0 0 0 0 1.3l1 1a.9.9 0 1 0 1.3-1.3l-1-1a.9.9 0 0 0-1.3 0zm10.5 10.5a.9.9 0 0 0 0 1.3l1 1a.9.9 0 1 0 1.3-1.3l-1-1a.9.9 0 0 0-1.3 0zM18.4 4.7a.9.9 0 0 0-1.3 0l-1 1a.9.9 0 1 0 1.3 1.3l1-1a.9.9 0 0 0 0-1.3zM7.9 15.2a.9.9 0 0 0-1.3 0l-1 1a.9.9 0 1 0 1.3 1.3l1-1a.9.9 0 0 0 0-1.3z"
                />
              </svg>
            )}
            <span className="cs-theme-toggle-label">{colorTheme === 'light' ? 'Dark' : 'Light'}</span>
          </button>
        )}
        {user ? (
          <>
            <AgentDock />
            <span className="cs-nav-email">{user.email}</span>
            <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => signOut()}>
              Sign out
            </button>
          </>
        ) : (
          <Link to="/login" className="cs-btn cs-btn-outline cs-btn-sm" onClick={close}>
            Sign in
          </Link>
        )}
      </div>
    </nav>
  );
}
